import { ActionResponseSchema } from "@bestowinc/tinkertoy-core";

import { TinkertoyActionError, VALIDATION_ERROR_CODE } from "./action-errors";
import { applyActionRedirect } from "./apply-action-redirect";

/**
 * Turn an HTTP JSON body into a result or throw an action error.
 *
 * The body must match the server's action response contract — one of
 * `success | validation_error | handler_error | error | redirect` (see
 * `ActionResponseSchema` in `@bestowinc/tinkertoy-core`).
 *
 * - `success` resolves with `data`.
 * - Error variants throw {@link TinkertoyActionError}.
 * - `redirect` with `external: true` (default) hard-navigates and returns a
 *   never-settling promise via {@link applyActionRedirect}.
 * - `redirect` with `external: false` throws {@link TinkertoyActionRedirectError}
 *   so the shell can SPA-navigate.
 *
 * Note: `ActionSuccessResponse.shouldRevalidate` is intentionally ignored
 * here (a separate ticket will thread it through to the router).
 */
export async function parseActionResponseBody(json: unknown): Promise<unknown> {
  if (typeof json !== "object" || json === null) {
    throw new TinkertoyActionError("Action response was not a JSON object.");
  }

  const result = ActionResponseSchema.safeParse(json);

  if (!result.success) {
    throw new TinkertoyActionError("Unrecognized action response shape.");
  }

  const parsed = result.data;

  switch (parsed.type) {
    case "success":
      return parsed.data;

    case "redirect":
      return applyActionRedirect(parsed.location, parsed.external);

    case "validation_error":
      throw new TinkertoyActionError(parsed.message, {
        code: VALIDATION_ERROR_CODE,
        validationErrors:
          Object.keys(parsed.errors).length > 0 ? parsed.errors : undefined,
      });

    case "handler_error":
      throw new TinkertoyActionError(parsed.message, {
        code: parsed.code ?? "HANDLER_ERROR",
      });

    case "error":
      throw new TinkertoyActionError(parsed.message, { code: parsed.code });
  }
}
